import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { DataWarningIcon } from '@/components/metrics/data-warning-icon';
import type { DataSufficiencyState } from '@/components/metrics/metric-sections.types';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { CardHeader, CardTitle } from '@/components/ui/card';

interface ChartCardHeaderProps {
  title: string;
  total?: string;
  helpKey: string;
  rightContent?: ReactNode;
  dataSufficiency?: DataSufficiencyState;
  isLoadingDataSufficiency?: boolean;
  /** When provided, the total becomes a button. */
  onTotalClick?: () => void;
  totalAriaLabel?: string;
  /** Next to the total: the variation against the previous period (CMP-01). */
  delta?: ReactNode;
  /** Line under the total (e.g. the Recompras sentence). */
  description?: ReactNode;
}

export function ChartCardHeader({
  title,
  total,
  helpKey,
  rightContent,
  dataSufficiency,
  isLoadingDataSufficiency = false,
  onTotalClick,
  totalAriaLabel,
  delta,
  description,
}: ChartCardHeaderProps) {
  const displayTotal =
    dataSufficiency && !dataSufficiency.canDisplay ? '--' : total;

  return (
    <CardHeader className="space-y-2">
      <div className="flex flex-row justify-between items-start gap-2">
        <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <span className="leading-none">{title}</span>
          <HelpIconButton helpKey={helpKey} />
          {dataSufficiency?.shouldShowWarning ? (
            <DataWarningIcon
              version={dataSufficiency.minAppVersion}
              percentage={Math.round(
                dataSufficiency.activeUsersOnOrAfterVersion.percentage * 100
              )}
            />
          ) : null}
        </CardTitle>
        {rightContent}
      </div>
      {isLoadingDataSufficiency ? (
        <Loader2 className="h-6 w-6 animate-spin" />
      ) : (
        displayTotal && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {onTotalClick && displayTotal !== '--' ? (
              <button
                type="button"
                className="w-fit cursor-pointer rounded-md text-left text-2xl font-semibold tracking-tight hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={totalAriaLabel}
                onClick={onTotalClick}
              >
                {displayTotal}
              </button>
            ) : (
              <p className="text-2xl font-semibold tracking-tight">
                {displayTotal}
              </p>
            )}
            {displayTotal !== '--' ? delta : null}
          </div>
        )
      )}
      {description ? (
        <p
          data-chart-card-description
          className="text-xs text-muted-foreground"
        >
          {description}
        </p>
      ) : null}
    </CardHeader>
  );
}
